'use client'

import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {File as FileIcon, Folder, Image as ImageIcon, Mic, Search, Smile, Trash2, Users, Zap} from 'lucide-react'
import type {ReplyPayload} from '@/types'
import {apiGet} from '@/lib/api'
import {formatFileSize, getVideoPoster, isHeicFile, isImageFile, readFileAsDataUrl, readImageFileAsSendableDataUrl} from '@/lib/utils'
import {emojiCategories} from '@/lib/emoji-data'
import {message} from '@/components/Toast'
import {TtsButton} from '@/components/TtsButton'
import {encodeVoiceFileForWechat} from '@/lib/voice-codec'

type VoiceRecordingSession = {
    stream: MediaStream
    audioContext: AudioContext
    source: MediaStreamAudioSourceNode
    processor: ScriptProcessorNode
    chunks: Float32Array[]
    sampleRate: number
    startedAt: number
}

type BrowserAudioWindow = Window &
    typeof globalThis & {
    webkitAudioContext?: typeof AudioContext
}

const MAX_VOICE_RECORD_SECONDS = 30

export type MentionOption = {
    wxid: string
    label: string
    avatar?: string
}

type AddVoiceFileOptions = {
    clearContent?: boolean
    skipVoiceChanger?: boolean
}

function formatDuration(totalSeconds: number): string {
    const total = Math.max(0, Math.floor(totalSeconds))
    const minutes = Math.floor(total / 60)
    const seconds = total % 60
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function mergeFloat32Chunks(chunks: Float32Array[]): Float32Array {
    const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
    const merged = new Float32Array(total)
    let offset = 0
    for (const chunk of chunks) {
        merged.set(chunk, offset)
        offset += chunk.length
    }
    return merged
}

function getAudioDuration(file: File): Promise<number> {
    return new Promise((resolve) => {
        const AudioContextCtor =
            window.AudioContext || (window as BrowserAudioWindow).webkitAudioContext
        if (!AudioContextCtor) {
            resolve(1)
            return
        }
        const ctx = new AudioContextCtor()
        const reader = new FileReader()
        reader.onload = () => {
            ctx.decodeAudioData(reader.result as ArrayBuffer, (buf) => {
                resolve(Math.max(1, Math.round(buf.duration)))
                void ctx.close()
            }, () => {
                resolve(1)
                void ctx.close()
            })
        }
        reader.onerror = () => resolve(1)
        reader.readAsArrayBuffer(file)
    })
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
    const bytesPerSample = 2
    const dataLength = samples.length * bytesPerSample
    const buffer = new ArrayBuffer(44 + dataLength)
    const view = new DataView(buffer)
    const writeText = (offset: number, value: string) => {
        for (let i = 0; i < value.length; i += 1) {
            view.setUint8(offset + i, value.charCodeAt(i))
        }
    }
    writeText(0, 'RIFF')
    view.setUint32(4, 36 + dataLength, true)
    writeText(8, 'WAVE')
    writeText(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, 1, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, sampleRate * bytesPerSample, true)
    view.setUint16(32, bytesPerSample, true)
    view.setUint16(34, 16, true)
    writeText(36, 'data')
    view.setUint32(40, dataLength, true)
    let offset = 44
    for (const sample of samples) {
        const clamped = Math.max(-1, Math.min(1, sample))
        view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
        offset += bytesPerSample
    }
    return new Blob([buffer], {type: 'audio/wav'})
}

export function ReplyPayloadEditor({
    payload,
    onChange,
    compact = false,
    fill = false,
    action,
    afterQuickReply,
    onSubmit,
    placeholder = '输入消息',
    voiceId,
    mentionOptions = [],
}: {
    payload: ReplyPayload
    onChange: (payload: ReplyPayload) => void
    compact?: boolean
    /**
     * When true, the textarea grows to fill its parent — useful inside
     * the realtime conversation composer where the user can drag the
     * whole block taller. Without it the editor stays at its natural
     * `min-h-[120px]` size, which is right for the settings cards.
     */
    fill?: boolean
    action?: ReactNode
    afterQuickReply?: ReactNode
    onSubmit?: () => void
    placeholder?: string
    voiceId?: string
    mentionOptions?: MentionOption[]
}) {
    const parts = payload.parts || []
    const [showEmoji, setShowEmoji] = useState(false)
    const [emojiTab, setEmojiTab] = useState(0)
    const [emojiPos, setEmojiPos] = useState({top: 0, left: 0})
    const [mounted, setMounted] = useState(false)
    const emojiBtnRef = useRef<HTMLDivElement>(null)
    const emojiPanelRef = useRef<HTMLDivElement>(null)
    const imageInputRef = useRef<HTMLInputElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const voiceRecordingRef = useRef<VoiceRecordingSession | null>(null)
    const voiceTimerRef = useRef<number | null>(null)
    const voiceAutoStopRef = useRef<number | null>(null)
    const [voiceRecording, setVoiceRecording] = useState(false)
    const [voiceRecordingSeconds, setVoiceRecordingSeconds] = useState(0)
    const [mentionOpen, setMentionOpen] = useState(false)
    const [mentionQuery, setMentionQuery] = useState('')
    const [mentionTriggerStart, setMentionTriggerStart] = useState(-1)
    const [mentionActiveIndex, setMentionActiveIndex] = useState(-1)
    const currentCategory = emojiCategories[emojiTab]
    const latestPayloadRef = useRef(payload)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const mentionPanelRef = useRef<HTMLDivElement>(null)

    const filteredMentionOptions = useMemo(() => {
        const query = mentionQuery.trim().toLowerCase()
        if (!query) return mentionOptions
        return mentionOptions.filter((option) =>
            option.label.toLowerCase().includes(query) || option.wxid.toLowerCase().includes(query),
        )
    }, [mentionOptions, mentionQuery])

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        latestPayloadRef.current = payload
    }, [payload])

    useEffect(() => {
        if (!mentionOpen) return
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node
            if (mentionPanelRef.current?.contains(target)) return
            if (textareaRef.current?.contains(target)) return
            setMentionOpen(false)
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [mentionOpen])

    useEffect(() => {
        if (mentionOpen && mentionOptions.length === 0) setMentionOpen(false)
    }, [mentionOpen, mentionOptions.length])

    const update = (patch: Partial<ReplyPayload>) => onChange({...payload, type: 'text', ...patch})

    const closeMentionPanel = () => {
        setMentionOpen(false)
        setMentionQuery('')
        setMentionTriggerStart(-1)
        setMentionActiveIndex(-1)
    }

    const detectMention = (content: string, caret: number) => {
        if (mentionOptions.length === 0) {
            closeMentionPanel()
            return
        }
        const beforeCaret = content.slice(0, caret)
        const match = beforeCaret.match(/@([^\s@]*)$/)
        if (!match) {
            closeMentionPanel()
            return
        }
        const triggerStart = beforeCaret.lastIndexOf('@')
        setMentionTriggerStart(triggerStart)
        setMentionQuery(match[1] || '')
        setMentionActiveIndex(-1)
        setMentionOpen(true)
    }

    const handleTextChange = (content: string, caret: number) => {
        const atLabels = payload.atLabels || {}
        const at = (payload.at || []).filter((wxid) => {
            const label = atLabels[wxid]
            return !label || content.includes(`@${label}`)
        })
        const nextLabels = Object.fromEntries(
            Object.entries(atLabels).filter(([wxid]) => at.includes(wxid)),
        )
        update({content, at, atLabels: nextLabels})
        detectMention(content, caret)
    }

    const selectMention = (option: MentionOption) => {
        if (mentionTriggerStart < 0) return
        const textarea = textareaRef.current
        const content = payload.content || ''
        const caret = textarea?.selectionStart ?? content.length
        const inserted = `@${option.label} `
        const nextContent = content.slice(0, mentionTriggerStart) + inserted + content.slice(caret)
        const currentAt = payload.at || []
        const nextAt = option.wxid === 'notify@all'
            ? ['notify@all']
            : Array.from(new Set([...currentAt, option.wxid]))
        const nextLabels = option.wxid === 'notify@all'
            ? {'notify@all': option.label}
            : {...(payload.atLabels || {}), [option.wxid]: option.label}
        update({content: nextContent, at: nextAt, atLabels: nextLabels})
        closeMentionPanel()
        const nextCaret = mentionTriggerStart + inserted.length
        requestAnimationFrame(() => {
            textarea?.focus()
            textarea?.setSelectionRange(nextCaret, nextCaret)
        })
    }

    const openEmoji = useCallback(() => {
        if (emojiBtnRef.current) {
            const rect = emojiBtnRef.current.getBoundingClientRect()
            setEmojiPos({top: rect.top, left: rect.left + rect.width / 2})
        }
        setShowEmoji(true)
    }, [])

    useEffect(() => {
        if (!showEmoji) return
        const handler = (e: MouseEvent) => {
            const target = e.target as Node
            if (emojiBtnRef.current?.contains(target)) return
            if (emojiPanelRef.current?.contains(target)) return
            setShowEmoji(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [showEmoji])

    const addPart = (part: ReplyPayload) =>
        onChange({...payload, type: 'text', parts: [...parts, part]})
    const removePart = (index: number) =>
        onChange({
            ...payload,
            type: 'text',
            parts: parts.filter((_, partIndex) => partIndex !== index),
        })
    const addEmoji = (emoji: string) => {
        update({content: `${payload.content || ''}${emoji}`})
        setShowEmoji(false)
    }
    const addVoiceFile = async (file: File, durationSeconds = 1, options: AddVoiceFileOptions = {}) => {
        const previewBase64 = await readFileAsDataUrl(file)
        const part: ReplyPayload = {
            type: 'voice',
            base64: previewBase64,
            content: '',
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type || 'audio/wav',
            previewBase64,
            rawBase64: previewBase64,
            playLength: Math.max(1, Math.round(durationSeconds)),
            skipVoiceChanger: options.skipVoiceChanger,
        }
        const nextPayload: ReplyPayload = {
            ...payload,
            type: 'text',
            content: options.clearContent ? '' : payload.content,
            parts: [...parts, part],
        }
        onChange(nextPayload)
        latestPayloadRef.current = nextPayload

        try {
            const encoded = await encodeVoiceFileForWechat(file)
            const current = latestPayloadRef.current
            const nextParts = (current.parts || []).map((item) =>
                item.previewBase64 === previewBase64 && item.rawBase64 === previewBase64
                    ? {
                        ...item,
                        base64: encoded.base64,
                        fileName: encoded.fileName,
                        fileSize: encoded.fileSize,
                        mimeType: encoded.mimeType,
                        voiceFormat: encoded.voiceFormat,
                    }
                    : item,
            )
            const encodedPayload = {...current, parts: nextParts}
            onChange(encodedPayload)
            latestPayloadRef.current = encodedPayload
        } catch (error) {
            message.error(error instanceof Error ? error.message : '语音编码失败')
        }
    }
    const addFile = async (file: File) => {
        const base = {fileName: file.name, fileSize: file.size, mimeType: file.type}
        if (isImageFile(file)) {
            try {
                const base64 = await readImageFileAsSendableDataUrl(file)
                addPart({
                    type: 'image',
                    base64,
                    content: '',
                    ...base,
                    mimeType: isHeicFile(file) ? 'image/jpeg' : file.type,
                })
            } catch (error) {
                message.error(error instanceof Error ? error.message : '图片转换失败')
            }
            return
        }
        const base64 = await readFileAsDataUrl(file)
        if (file.type.startsWith('audio/')) {
            const dur = await getAudioDuration(file)
            await addVoiceFile(file, dur)
            return
        }
        if (file.type.startsWith('video/')) {
            addPart({type: 'video', base64, imageBase64: await getVideoPoster(file), playLength: 1, content: '', ...base})
            return
        }
        addPart({type: 'file', base64, content: '', ...base})
    }

    const clearVoiceTimers = () => {
        if (voiceTimerRef.current !== null) {
            window.clearInterval(voiceTimerRef.current)
            voiceTimerRef.current = null
        }
        if (voiceAutoStopRef.current !== null) {
            window.clearTimeout(voiceAutoStopRef.current)
            voiceAutoStopRef.current = null
        }
    }

    const finishVoiceRecording = useCallback(async (shouldAttach: boolean) => {
        const session = voiceRecordingRef.current
        if (!session) return
        voiceRecordingRef.current = null
        clearVoiceTimers()
        setVoiceRecording(false)
        setVoiceRecordingSeconds(0)
        session.processor.disconnect()
        session.source.disconnect()
        session.stream.getTracks().forEach((track) => track.stop())
        void session.audioContext.close()

        if (!shouldAttach) return
        const seconds = Math.max(1, (Date.now() - session.startedAt) / 1000)
        const samples = mergeFloat32Chunks(session.chunks)
        if (samples.length < session.sampleRate * 0.3) {
            message.warning('录音时间太短')
            return
        }
        const blob = encodeWav(samples, session.sampleRate)
        const file = new File([blob], `voice-${Date.now()}.wav`, {type: 'audio/wav'})
        await addVoiceFile(file, seconds)
    }, [parts, payload])

    const startVoiceRecording = useCallback(async () => {
        if (voiceRecordingRef.current) return
        const AudioContextCtor =
            window.AudioContext || (window as BrowserAudioWindow).webkitAudioContext
        if (!navigator.mediaDevices?.getUserMedia || !AudioContextCtor) {
            message.warning('当前浏览器不支持录音')
            return
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                },
            })
            const audioContext = new AudioContextCtor()
            const source = audioContext.createMediaStreamSource(stream)
            const processor = audioContext.createScriptProcessor(4096, 1, 1)
            const chunks: Float32Array[] = []
            processor.onaudioprocess = (event) => {
                chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)))
                event.outputBuffer.getChannelData(0).fill(0)
            }
            source.connect(processor)
            processor.connect(audioContext.destination)
            voiceRecordingRef.current = {
                stream,
                audioContext,
                source,
                processor,
                chunks,
                sampleRate: audioContext.sampleRate,
                startedAt: Date.now(),
            }
            setVoiceRecording(true)
            setVoiceRecordingSeconds(0)
            voiceTimerRef.current = window.setInterval(() => {
                const startedAt = voiceRecordingRef.current?.startedAt
                if (!startedAt) return
                setVoiceRecordingSeconds(Math.floor((Date.now() - startedAt) / 1000))
            }, 250)
            voiceAutoStopRef.current = window.setTimeout(() => {
                message.info('录音已达到 30 秒')
                void finishVoiceRecording(true)
            }, MAX_VOICE_RECORD_SECONDS * 1000)
        } catch (err) {
            clearVoiceTimers()
            setVoiceRecording(false)
            message.error(err instanceof Error ? err.message : '无法开始录音')
        }
    }, [finishVoiceRecording])

    useEffect(() => {
        return () => {
            clearVoiceTimers()
            const session = voiceRecordingRef.current
            if (!session) return
            voiceRecordingRef.current = null
            session.processor.disconnect()
            session.source.disconnect()
            session.stream.getTracks().forEach((track) => track.stop())
            void session.audioContext.close()
        }
    }, [])

    const toggleVoiceRecording = () => {
        if (voiceRecording) {
            void finishVoiceRecording(true)
        } else {
            void startVoiceRecording()
        }
    }

    return (
        <div className={`flex flex-col gap-2 w-full ${fill ? 'h-full' : ''}`}>
            {/* 工具栏在上 — 与首页消息对话框一致，按钮紧凑排列 */}
            <div className="flex items-center justify-between gap-2 px-1 shrink-0">
                <div className="flex items-center gap-2 text-muted">
                    <div ref={emojiBtnRef} className="inline-flex">
                        <button
                            type="button"
                            title="表情"
                            onClick={openEmoji}
                            className="w-8 h-8 flex items-center justify-center rounded hover:bg-page hover:text-soft transition-colors"
                        >
                            <Smile size={20}/>
                        </button>
                    </div>
                    {mounted && showEmoji
                        ? createPortal(
                              <div
                                  ref={emojiPanelRef}
                                  className="fixed z-overlay w-[320px] bg-white border border-hair rounded-md shadow-card overflow-hidden"
                                  style={{
                                      top: emojiPos.top,
                                      left: emojiPos.left,
                                      transform: 'translate(-50%, -100%)',
                                      marginBottom: 8,
                                  }}
                              >
                                  <div className="flex border-b border-hair bg-page">
                                      {emojiCategories.map((cat, index) => (
                                          <button
                                              type="button"
                                              key={cat.name}
                                              className={`flex-1 h-9 text-base transition-colors ${
                                                  index === emojiTab
                                                      ? 'bg-white text-accent border-b-2 border-accent'
                                                      : 'text-soft hover:bg-white'
                                              }`}
                                              onClick={() => setEmojiTab(index)}
                                              title={cat.name}
                                          >
                                              {cat.icon}
                                          </button>
                                      ))}
                                  </div>
                                  <div className="px-3 py-2 text-xs text-muted border-b border-hair">
                                      {currentCategory.name}
                                  </div>
                                  <div className="grid grid-cols-8 gap-1 p-2 max-h-56 overflow-y-auto">
                                      {currentCategory.emojis.map((emoji) => (
                                          <button
                                              type="button"
                                              key={emoji}
                                              className="h-8 w-8 flex items-center justify-center text-xl rounded hover:bg-page"
                                              onClick={() => addEmoji(emoji)}
                                          >
                                              {emoji}
                                          </button>
                                      ))}
                                  </div>
                              </div>,
                              document.body,
                          )
                        : null}
                    <button
                        type="button"
                        title="图片"
                        onClick={() => imageInputRef.current?.click()}
                        className="w-8 h-8 flex items-center justify-center rounded hover:bg-page hover:text-soft transition-colors"
                    >
                        <ImageIcon size={20}/>
                    </button>
                    <button
                        type="button"
                        title="文件"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-8 h-8 flex items-center justify-center rounded hover:bg-page hover:text-soft transition-colors"
                    >
                        <Folder size={20}/>
                    </button>
                    <button
                        type="button"
                        title={voiceRecording ? '停止录音' : '录音'}
                        onClick={toggleVoiceRecording}
                        className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                            voiceRecording
                                ? 'text-red-500 bg-red-50 animate-pulse'
                                : 'hover:bg-page hover:text-soft'
                        }`}
                    >
                        <Mic size={20}/>
                    </button>
                    <TtsButton
                        getText={() => payload.content || ''}
                        voiceId={voiceId}
                        disabled={voiceRecording}
                        className="w-8 h-8 flex items-center justify-center rounded hover:bg-page hover:text-soft disabled:opacity-50 disabled:cursor-wait transition-colors"
                        onAttachVoice={({file, durationSeconds}) => {
                            void addVoiceFile(file, durationSeconds, {
                                clearContent: true,
                                skipVoiceChanger: true,
                            })
                        }}
                    />
                    <QuickReplyButton
                        onSelect={(content) => {
                            if (payload.type === 'text') {
                                onChange({...payload, content: (payload.content || '') + content})
                            }
                        }}
                    />
                    {afterQuickReply}
                    {voiceRecording ? (
                        <span className="ml-1 text-xs text-red-500 tabular-nums">
                            录音中 {formatDuration(voiceRecordingSeconds)}
                        </span>
                    ) : null}
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*,.heic,.heif"
                        multiple
                        className="hidden"
                        onChange={(event) => {
                            Array.from(event.target.files || []).forEach((file) => void addFile(file))
                            event.currentTarget.value = ''
                        }}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(event) => {
                            Array.from(event.target.files || []).forEach((file) => void addFile(file))
                            event.currentTarget.value = ''
                        }}
                    />
                </div>
            </div>

            {/* 附件预览区 */}
            {parts.length ? (
                <div className="flex flex-wrap gap-2 shrink-0">
                    {parts.map((part, index) => (
                        <div
                            key={`${part.type}-${index}`}
                            className="relative bg-white border border-hair rounded-md p-2 flex flex-col gap-2 w-48"
                        >
                            <div className="flex items-center justify-between">
                                <strong className="text-xs text-soft">
                                    {part.type === 'image'
                                        ? '图片'
                                        : part.type === 'voice'
                                          ? '语音'
                                          : part.type === 'video'
                                            ? '视频'
                                            : '文件'}
                                </strong>
                                <button
                                    type="button"
                                    className="p-1 rounded text-muted hover:bg-red-50 hover:text-red-500"
                                    onClick={() => removePart(index)}
                                    aria-label="移除附件"
                                >
                                    <Trash2 size={14}/>
                                </button>
                            </div>
                            {part.type === 'image' ? (
                                <img
                                    src={part.base64}
                                    alt={part.fileName || '图片'}
                                    className="w-full h-24 object-cover rounded"
                                />
                            ) : null}
                            {part.type === 'voice' ? (
                                <div className="flex items-center gap-2">
                                    <Mic size={28} className="text-muted shrink-0"/>
                                    <div className="flex-1 min-w-0">
                                        <strong className="block text-xs text-ink truncate">
                                            {part.fileName || '语音'}
                                        </strong>
                                        <span className="text-[11px] text-muted">
                                            {formatDuration(part.playLength || 1)}
                                        </span>
                                    </div>
                                </div>
                            ) : null}
                            {part.type === 'video' ? (
                                <div className="flex items-center gap-2">
                                    {part.imageBase64 ? (
                                        <img src={part.imageBase64} alt="" className="w-10 h-10 object-cover rounded"/>
                                    ) : (
                                        <FileIcon size={32} className="text-muted"/>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <strong
                                            className="block text-xs text-ink truncate">{part.fileName || '视频文件'}</strong>
                                        <span className="text-[11px] text-muted">{formatFileSize(part.fileSize)}</span>
                                    </div>
                                </div>
                            ) : null}
                            {part.type === 'file' ? (
                                <div className="flex items-center gap-2">
                                    <FileIcon size={32} className="text-muted shrink-0"/>
                                    <div className="flex-1 min-w-0">
                                        <strong
                                            className="block text-xs text-ink truncate">{part.fileName || '文件'}</strong>
                                        <span className="text-[11px] text-muted">{formatFileSize(part.fileSize)}</span>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
            ) : null}

            {/* 文本框 */}
            <div className={`relative ${fill ? 'flex-1 min-h-0' : ''}`}>
                {mentionOpen ? (
                    <div
                        ref={mentionPanelRef}
                        className="absolute z-overlay left-2 bottom-full mb-2 w-72 overflow-hidden rounded-md border border-hair bg-white shadow-card"
                        role="listbox"
                        aria-label="选择要@的群成员"
                    >
                        <div className="border-b border-hair p-2">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" size={14}/>
                                <input
                                    type="text"
                                    value={mentionQuery}
                                    onChange={(event) => {
                                        setMentionQuery(event.target.value)
                                        setMentionActiveIndex(-1)
                                    }}
                                    placeholder="搜索群成员"
                                    aria-label="搜索群成员"
                                    className="h-8 w-full rounded border border-hair bg-page pl-8 pr-3 text-sm text-ink outline-none placeholder:text-muted focus:border-accent"
                                />
                            </div>
                        </div>
                        <div className="max-h-56 overflow-y-auto">
                            {filteredMentionOptions.length ? filteredMentionOptions.map((option, index) => (
                                <button
                                    key={option.wxid}
                                    type="button"
                                    role="option"
                                    aria-label={option.label}
                                    aria-selected={index === mentionActiveIndex}
                                    className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                                        index === mentionActiveIndex ? 'bg-page text-accent' : 'text-ink hover:bg-page'
                                    }`}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => selectMention(option)}
                                >
                                    {option.wxid === 'notify@all' ? (
                                        <span
                                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-page text-muted"
                                            aria-label="所有人头像"
                                        >
                                            <Users size={17}/>
                                        </span>
                                    ) : option.avatar ? (
                                        <img src={option.avatar} alt={`${option.label}头像`} className="h-8 w-8 shrink-0 rounded object-cover"/>
                                    ) : (
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-page text-xs text-muted">
                                            {option.label.slice(0, 1)}
                                        </span>
                                    )}
                                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                                </button>
                            )) : (
                                <div className="px-3 py-4 text-center text-sm text-muted">没有匹配的群成员</div>
                            )}
                        </div>
                    </div>
                ) : null}
                <textarea
                    ref={textareaRef}
                    rows={compact ? 3 : 4}
                    value={payload.content || ''}
                    onChange={(event) => handleTextChange(event.target.value, event.target.selectionStart)}
                    onClick={(event) => detectMention(event.currentTarget.value, event.currentTarget.selectionStart)}
                    onKeyDown={(event) => {
                        if (mentionOpen) {
                            if (event.key === 'Escape') {
                                event.preventDefault()
                                closeMentionPanel()
                                return
                            }
                            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                                event.preventDefault()
                                const direction = event.key === 'ArrowDown' ? 1 : -1
                                setMentionActiveIndex((current) => {
                                    const length = filteredMentionOptions.length
                                    if (!length) return -1
                                    if (current < 0) return direction > 0 ? 0 : length - 1
                                    return (current + direction + length) % length
                                })
                                return
                            }
                            if (event.key === 'Enter' && filteredMentionOptions.length) {
                                event.preventDefault()
                                selectMention(filteredMentionOptions[mentionActiveIndex] || filteredMentionOptions[0])
                                return
                            }
                        }
                        if (event.key === 'Enter' && !event.shiftKey && onSubmit) {
                            event.preventDefault()
                            onSubmit()
                        }
                    }}
                    placeholder={placeholder}
                    className={`w-full resize-none border-0 bg-transparent px-2 py-2 text-sm leading-relaxed text-ink outline-none placeholder:text-muted ${
                        fill ? 'h-full min-h-0' : 'min-h-[120px]'
                    }`}
                />
            </div>

            {/* 右下角发送按钮 */}
            {action ? (
                <div className="flex justify-end shrink-0">{action}</div>
            ) : null}
        </div>
    )
}

type QuickReplyItem = {
    id: string
    title?: string
    content: string
}

function QuickReplyButton({onSelect}: {onSelect: (content: string) => void}) {
    const [replies, setReplies] = useState<QuickReplyItem[]>([])
    const [open, setOpen] = useState(false)
    const btnRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        apiGet<{success: boolean; replies: QuickReplyItem[]}>('/quick-replies', {silent: true})
            .then((res) => setReplies(res.replies || []))
            .catch(() => {})
    }, [])

    useEffect(() => {
        if (!open) return
        function handleClick(e: MouseEvent) {
            if (btnRef.current?.contains(e.target as Node)) return
            if (panelRef.current?.contains(e.target as Node)) return
            setOpen(false)
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [open])

    if (replies.length === 0) return null

    return (
        <div className="relative inline-flex">
            <button
                ref={btnRef}
                type="button"
                title="快捷回复"
                onClick={() => setOpen(!open)}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-page hover:text-soft transition-colors"
            >
                <Zap size={20}/>
            </button>
            {open && (
                <div
                    ref={panelRef}
                    className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border border-hair shadow-lg rounded-lg py-1 z-50 w-64 max-h-56 overflow-y-auto"
                >
                    {replies.map((reply) => {
                        const title = reply.title?.trim() || reply.content
                        return (
                        <div key={reply.id}>
                            <button
                                type="button"
                                onClick={() => { onSelect(reply.content); setOpen(false) }}
                                className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-gray-50 transition-colors truncate"
                                title={reply.content}
                            >
                                {title}
                            </button>
                        </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
